import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Select, Table, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .search-input {
    width: 220px;
    border-radius: 20px;
  }
`;

// Existing rows (created before this page existed, likely via direct API
// testing) use inconsistent formats - "24-10-2025", "29/11/2025" - which
// dayjs() can't parse on its own (returns Invalid Date). New rows from this
// page always send ISO (YYYY-MM-DD), which parses fine. Try ISO first, fall
// back to DD-MM-YYYY / DD/MM/YYYY.
const parseFlexibleDate = (value) => {
  if (!value) return null;
  const iso = dayjs(value);
  if (iso.isValid()) return iso;

  const match = String(value).match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    const parsed = dayjs(`${year}-${month}-${day}`);
    if (parsed.isValid()) return parsed;
  }

  return null;
};

const formatDate = (value) => {
  const parsed = parseFlexibleDate(value);
  return parsed ? parsed.format("Do MMM YYYY") : value || "-";
};

const Conference = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const { data, loading, request } = UseApi();
  const { request: peopleRequest } = UseApi();
  const { request: attendeesRequest } = UseApi();
  const { request: createRequest, loading: creating } = UseApi();
  const { request: updateRequest, loading: updating } = UseApi();
  const { request: deleteRequest } = UseApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingConference, setEditingConference] = useState(null);
  const [form] = Form.useForm();

  const fetchConferences = () => {
    if (!clientId) return;
    // UseApi's request() rethrows on failure - uncaught here, this was a
    // genuine unhandled promise rejection (visible as CRA's dev error
    // overlay whenever this endpoint fails). The loading/error state
    // this hook already exposes is all the UI needs.
    request({ endPoint: `/conference/getconference/${clientId}`, method: "get" }).catch(() => {});
  };

  useEffect(() => {
    fetchConferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const conferences = data?.response || [];

  const filteredConferences = useMemo(() => {
    return conferences.filter((item) => {
      const matchesSearch = (item.conference_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const parsedStart = parseFlexibleDate(item.start_date);
      const matchesDate =
        !dateFilter || (parsedStart && parsedStart.isSame(dateFilter, "day"));
      return matchesSearch && matchesDate;
    });
  }, [conferences, searchTerm, dateFilter]);

  const columns = [
    { title: "Name", dataIndex: "conference_name", key: "conference_name" },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: formatDate,
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) =>
        record.duration ? `${record.duration} ${record.hr_minute || ""}` : "-",
    },
    {
      title: "No. of Employees",
      key: "employee_added",
      // Column holds either a plain count (rows inserted before the
      // frontend existed) or the array this page now sends - render
      // whichever shape is actually there instead of assuming one.
      render: (_, record) => {
        const value = record.employee_added;
        if (Array.isArray(value)) return value.length;
        if (typeof value === "number") return value;
        return "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 12 }}>
          <EditOutlined onClick={() => openEditModal(record)} />
          <Popconfirm
            title="Delete this conference?"
            onConfirm={() => handleDelete(record.conference_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <DeleteOutlined style={{ color: "#ff4d4f" }} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const loadPeople = async () => {
    const result = await peopleRequest({
      endPoint: `/people/getuser/${clientId}`,
      method: "get",
    }).catch(() => null);
    setPeople(result?.response || []);
  };

  const openCreateModal = async () => {
    setEditingConference(null);
    form.resetFields();
    setOpen(true);
    await loadPeople();
  };

  const openEditModal = async (record) => {
    setEditingConference(record);
    setOpen(true);
    const [, attendeesResult] = await Promise.all([
      loadPeople(),
      attendeesRequest({
        endPoint: `/conference/getconferenceattendees/${clientId}/${record.conference_id}`,
        method: "get",
      }).catch(() => null),
    ]);
    const attendeePeopleIds = (attendeesResult?.response || []).map((p) => p.people_id);
    const parsedStart = parseFlexibleDate(record.start_date);
    form.setFieldsValue({
      conference_name: record.conference_name,
      start_date: parsedStart,
      start_time: record.start_time,
      duration: record.duration,
      hr_minute: record.hr_minute,
      conference_url: record.conference_url,
      comment: record.comment,
      employee_added: attendeePeopleIds,
    });
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        conference_name: values.conference_name,
        start_date: values.start_date.format("YYYY-MM-DD"),
        start_time: values.start_time,
        duration: values.duration,
        hr_minute: values.hr_minute,
        conference_url: values.conference_url,
        comment: values.comment,
        // Real list of picked people ids now - the backend stores
        // these in tymeplushrlmsconferenceperson (a proper join table,
        // see db/schema/create_conference_attendees.sql) instead of
        // the old integer-only employee_added column.
        employee_added: values.employee_added || [],
      };
      if (editingConference) {
        await updateRequest({
          endPoint: `/conference/updateconference/${clientId}/${editingConference.conference_id}`,
          method: "patch",
          data,
        });
        message.success("Conference updated successfully");
      } else {
        await createRequest({
          endPoint: `/conference/createconference/${clientId}`,
          method: "post",
          data,
        });
        message.success("Conference created successfully");
      }
      setOpen(false);
      setEditingConference(null);
      fetchConferences();
    } catch (error) {
      // A failed form.validateFields() lands here too - antd already
      // renders those inline under each field, so only surface a toast
      // for an actual API failure (errorFields is antd's own shape for
      // a validation rejection).
      if (!error?.errorFields) {
        message.error(editingConference ? "Failed to update conference" : "Failed to create conference");
      }
    }
  };

  const handleDelete = async (conferenceId) => {
    try {
      await deleteRequest({
        endPoint: `/conference/deleteconference/${clientId}/${conferenceId}`,
        method: "delete",
      });
      message.success("Conference deleted successfully");
      fetchConferences();
    } catch (error) {
      message.error("Failed to delete conference");
    }
  };

  return (
    <Wrapper>
      <div className="header-row">
        <div className="title">Conference</div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Create Conference
        </Button>
      </div>

      <div className="toolbar">
        <Input
          className="search-input"
          placeholder="Search"
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <DatePicker
          placeholder="Date"
          value={dateFilter}
          onChange={(date) => setDateFilter(date)}
          allowClear
        />
      </div>

      <div>Total Conference: {filteredConferences.length}</div>

      <Table
        rowKey={(record, index) => record.conference_id ?? index}
        columns={columns}
        dataSource={filteredConferences}
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editingConference ? "Edit Conference" : "Create Conference"}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditingConference(null);
        }}
        onOk={handleCreate}
        confirmLoading={creating || updating}
        okText={editingConference ? "Save" : "Create"}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="conference_name"
            label="Conference Name"
            rules={[{ required: true, message: "Conference name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="start_date"
            label="Start Date"
            rules={[{ required: true, message: "Start date is required" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="start_time" label="Start Time">
            <Input placeholder="e.g. 10:00 AM" />
          </Form.Item>
          <Form.Item name="duration" label="Duration">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="hr_minute" label="Duration Unit">
            <Select
              options={[
                { label: "Minutes", value: "mins" },
                { label: "Hours", value: "hrs" },
              ]}
            />
          </Form.Item>
          <Form.Item name="conference_url" label="Conference URL">
            <Input placeholder="https://" />
          </Form.Item>
          <Form.Item name="comment" label="Comment">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="employee_added" label="Employees">
            <Select
              mode="multiple"
              placeholder="Select employees"
              optionFilterProp="label"
              options={people.map((person) => ({
                value: person.people_id,
                label: `${person.first_name || ""} ${person.last_name || ""}`.trim(),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default Conference;
